// @ts-nocheck
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useQueryClient } from 'react-query';
import { Header } from 'sonata-platform-components';
import {
  ButtonUI,
  Input,
  Switch,
  showNotification,
  Tooltip,
  TooltipInteractionEnum,
  useQueryFetch
} from 'sonata-react-components';

import {
  addReportingTemplates,
  deleteReportingTemplates,
  getReportingTemplates
} from '@/ts-resources/reporting/reportingTemplate.resources';
import { useReportDownloadSectionContext } from '../ReportDownloadSection.context';
import DeleteTemplatePromt from './DeleteTemplatePromt';
import { ReportingDownloadHeaderStyle } from './reportingDownloadHeaderStyle.style';

import type { ReportingTemplate } from '@/ts-modules/reporting/ReportDownload/types';

type State = {
  loadedTemplateKey: string;
  templateIdToRemove?: string;
  templateName: string;
};

type Props = {
  id: string;
  onBackClick: () => void;
};

function ReportingDownloadHeader({ id, onBackClick }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();

  const {
    downloadReport,
    isDisabled,
    isLoading,
    isLoadingSchedule,
    isSchedule,
    isScheduleUpdate,
    isScheduleValid,
    reset,
    saveAsNewSchedule,
    saveChangeSchedule,
    setTabOptions,
    setType,
    tabsOptions,
    type,
    userId
  } = useReportDownloadSectionContext();

  const [{ loadedTemplateKey, templateIdToRemove, templateName }, setState] = useState<State>({
    loadedTemplateKey: '',
    templateName: ''
  });

  const { data: templates, isFetching } = useQueryFetch<{ amount: number; items: ReportingTemplate[] }>(
    ['getReportingTemplates', userId],
    async () => getReportingTemplates(userId)
  );

  const deleteTemplate = async () => {
    try {
      const templateId = templateIdToRemove as string;
      setState(prev => ({
        ...prev,
        templateIdToRemove: undefined
      }));
      await deleteReportingTemplates(userId, templateId);
      await queryClient.resetQueries(['getReportingTemplates', userId], {
        exact: true
      });
      showNotification(
        'success',
        formatMessage({
          defaultMessage: 'Template deleted',
          id: 'reporting.reportingDownload.deleted'
        })
      );
    } catch (_e) {
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'Template delete failed',
          id: 'reporting.reportingDownload.failedDelete'
        })
      );
    }
  };

  const handleTemplateNameChange = (name: string) => {
    setState(prev => ({
      ...prev,
      templateName: name
    }));
  };

  const handleSaveTemplateClick = async () => {
    try {
      await addReportingTemplates(userId, templateName, type, tabsOptions);
      handleTemplateNameChange('');
      await queryClient.resetQueries(['getReportingTemplates', userId], {
        exact: true
      });
      showNotification(
        'success',
        formatMessage({
          defaultMessage: 'Template saved',
          id: 'reporting.reportingDownload.saved'
        })
      );
    } catch {
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'Template creation failed',
          id: 'reporting.reportingDownload.failed'
        })
      );
    }
  };

  const handleLoadTemplateClick = (item: any) => {
    const tabs = item.tabsOptions.reduce((acc: any, tab: any) => {
      return {
        ...acc,
        [tab.sheetName]: {
          ...tab,
          columns: tab.columns.map((column: { visible: any }) => ({
            ...column,
            checked: column.visible
          })),
          options: {
            ...tab.options,
            groups: [...tab.options.groups]
          }
        }
      };
    }, {});
    setTabOptions(tabs);
    setType(item.type);
    setState(prev => ({
      ...prev,
      loadedTemplateKey: `${item._id}${(new Date()).toISOString()}`
    }));
  };

  const handleDeleteTemplateClick = (templateId: string) => {
    setState(prev => ({
      ...prev,
      templateIdToRemove: templateId
    }));
  };

  const handleCancelDeleteTemplateClick = () => {
    setState(prev => ({
      ...prev,
      templateIdToRemove: undefined
    }));
  };

  const handleSaveAsNewScheduleClick = () => {
    saveAsNewSchedule(onBackClick);
  };

  const handleSaveChangeScheduleClick = () => {
    saveChangeSchedule(onBackClick);
  };

  return (
    <ReportingDownloadHeaderStyle className="reportingHeader" id={id}>
      <Header>
        <Header.Title>
          <div className="reportingHeader__backLink" onClick={onBackClick}>
            <i className="fal fa-arrow-left" />
          </div>
          {formatMessage({
            defaultMessage: 'Data Downloader',
            id: 'reporting.reportingDownload.title'
          })}
          <Tooltip
            config={{
              arrow: true,
              component: templates?.amount ? (
                <div className="reportingHeader__menu">
                  {templates?.items.map(item => (
                    <div
                      className="reportingHeader__menuTemplate"
                      key={item._id}
                      onClick={() => handleLoadTemplateClick(item)}
                    >
                      <span className="reportingHeader__menuTemplateName">{item.name}</span>
                      <div className="reportingHeader__menuSheet">
                        {`(${item.tabsOptions.length} ${formatMessage({
                          defaultMessage: 'Sheets',
                          id: 'reporting.reportingDownload.sheets'
                        })})`}
                      </div>
                      <ButtonUI
                        className="reportingHeader__menuButton"
                        icon="fa-solid fa-trash"
                        onClick={() => handleDeleteTemplateClick(item._id)}
                        shape="link"
                        size="ultraSmall"
                        type="secondary"
                      />
                    </div>
                  ))}
                </div>
              ) : null,
              forcePlacement: true,
              interactions: [TooltipInteractionEnum.click],
              padding: '0',
              placement: 'bottom-end',
              strategy: 'fixed'
            }}
            key={loadedTemplateKey}
          >
            <ButtonUI
              className="reportingHeader__load"
              disabled={!templates?.items?.length}
              icon="fa-solid fa-arrow-up-from-dotted-line"
              shape="link"
              size="small"
              text="Load Template"
              type="secondary"
            />
          </Tooltip>
        </Header.Title>
        <Header.Actions>
          <ButtonUI
            className="reportingHeader__reset"
            icon="fa-solid fa-rotate-left"
            onClick={reset}
            shape="link"
            size="small"
            text={formatMessage({
              defaultMessage: 'Reset',
              id: 'reporting.reportingDownload.reset'
            })}
            type="secondary"
          />
          <Tooltip
            config={{
              arrow: true,
              component: (
                <div className="reportingHeader__template">
                  <Input
                    className="reportingHeader__templateInput"
                    id="reportingHeader-template"
                    inlineLabel
                    name="reportingHeader-template"
                    onChange={(val: any) => handleTemplateNameChange(val as string)}
                    placeholder={formatMessage({
                      defaultMessage: 'Template Name',
                      id: 'reporting.reportingDownload.input'
                    })}
                    shape="squareUI"
                    type="text"
                    value={templateName}
                  />
                  <ButtonUI
                    disabled={!templateName}
                    onClick={handleSaveTemplateClick}
                    shape="squareRounded"
                    text={formatMessage({
                      defaultMessage: 'Save',
                      id: 'common.save'
                    })}
                    type="primary"
                  />
                </div>
              ),
              forcePlacement: true,
              interactions: [TooltipInteractionEnum.click],
              padding: '0',
              placement: 'bottom-end',
              strategy: 'fixed'
            }}
            key={isFetching ? 'key-1' : 'key-2'}
          >
            <ButtonUI
              className="reportingHeader__save"
              disabled={isFetching}
              icon="fa-regular fa-floppy-disk"
              shape="link"
              size="small"
              text={formatMessage({
                defaultMessage: 'Save as Template',
                id: 'reporting.reportingDownload.saveTemplate'
              })}
              type="secondary"
            />
          </Tooltip>
          <ButtonUI
            disabled={isDisabled}
            loading={isLoading}
            loadingLabel={formatMessage({
              defaultMessage: 'Downloading',
              id: 'reporting.reportingDownload.actionLoading'
            })}
            onClick={downloadReport}
            size="small"
            text={formatMessage({
              defaultMessage: 'Download File',
              id: 'reporting.reportingDownload.action'
            })}
            type="secondary"
          />
          <Switch>
            <Switch.Case condition={isSchedule && isScheduleUpdate}>
              <Tooltip
                config={{
                  arrow: true,
                  component: (
                    <div className="reportingHeader__menu">
                      <ButtonUI
                        className="reportingHeader__titleMenuButton"
                        icon="fa-regular fa-floppy-disk"
                        onClick={handleSaveChangeScheduleClick}
                        shape="link"
                        text={formatMessage({
                          defaultMessage: 'Save Changes',
                          id: 'reporting.reportingDownload.saveChanges'
                        })}
                        type="secondary"
                      />
                      <ButtonUI
                        className="reportingHeader__titleMenuButton"
                        icon="fa-solid fa-plus"
                        onClick={handleSaveAsNewScheduleClick}
                        shape="link"
                        text={formatMessage({
                          defaultMessage: 'Save as New',
                          id: 'reporting.reportingDownload.saveAsNew'
                        })}
                        type="secondary"
                      />
                    </div>
                  ),
                  forcePlacement: true,
                  interactions: [TooltipInteractionEnum.click],
                  padding: '0',
                  placement: 'bottom-end',
                  strategy: 'fixed'
                }}
              >
                <ButtonUI
                  disabled={!isScheduleValid}
                  loading={isLoadingSchedule}
                  shape="squareRounded"
                  size="small"
                  text={formatMessage({
                    defaultMessage: 'Save Settings',
                    id: 'reporting.reportingDownload.saveSettings'
                  })}
                  type="primary"
                />
              </Tooltip>
            </Switch.Case>
            <Switch.Case condition={isSchedule && !isScheduleUpdate}>
              <ButtonUI
                disabled={!isScheduleValid}
                icon="fa-solid fa-plus"
                loading={isLoadingSchedule}
                onClick={handleSaveAsNewScheduleClick}
                shape="squareRounded"
                size="small"
                text={formatMessage({
                  defaultMessage: 'Save as New',
                  id: 'reporting.reportingDownload.saveAsNew'
                })}
                type="primary"
              />
            </Switch.Case>
          </Switch>
          {templateIdToRemove && (
            <DeleteTemplatePromt
              id={`${id}-DeleteTemplate`}
              onClose={handleCancelDeleteTemplateClick}
              onConfirm={deleteTemplate}
            />
          )}
        </Header.Actions>
      </Header>
    </ReportingDownloadHeaderStyle>
  );
}

export default ReportingDownloadHeader;
